'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@/src/components/ui';
import { noBorderStyles, TAGS } from '@/src/constants/constants';
import { Badge } from '@workspace/ui/components/badge';
import { cn } from '@workspace/ui/lib/utils';
import { SafeIcon } from '@/src/components/ui/SafeIcon';

interface Props {
  tags: string[];
  setTags: (newTags: string[]) => void;
  readonly?: boolean;
}

export const TagsProjectInput = ({
  tags,
  setTags,
  readonly = false,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredTags = TAGS.filter(
    ({ name }) =>
      name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.includes(name)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (tagName: string) => {
    if (tagName && !tags.includes(tagName) && tags.length < 3) {
      setTags([...tags, tagName]);
      setInputValue('');
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const removeTag = (indexToRemove: number) => {
    setTags(tags.filter((_, idx) => idx !== indexToRemove));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (tags.length < 3) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && filteredTags[focusedIndex]) {
        addTag(filteredTags[focusedIndex].name);
      } else if (inputValue.trim()) {
        addTag(inputValue.trim());
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < filteredTags.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const highlightMatch = (text: string, query: string) => {
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1 || !query.trim()) return text;
    return (
      <>
        {text.substring(0, index)}
        <span className="text-blue-primary font-semibold">
          {text.substring(index, index + query.length)}
        </span>
        {text.substring(index + query.length)}
      </>
    );
  };

  const maxTags = 3;
  const remainingTags = maxTags - tags.length;
  const isMaxReached = tags.length >= maxTags;
  const showPlaceholder = tags.length === 0 && inputValue.length === 0;

  return (
    <div className={cn('w-full mt-3 max-w-[670px] relative md:mt-3', {})}>
      <div
        ref={containerRef}
        onClick={() => inputRef.current?.focus()}
        className="w-full cursor-text flex justify-between"
      >
        <div
          className={cn('flex gap-2 md:flex-nowrap', {
            'md:hidden': remainingTags === 3,
          })}
        >
          {tags.map((tag, index) => (
            <Badge
              key={tag}
              variant="secondary"
              className="bg-bg-gray text-sm py-1 px-2 flex gap-1 transition-colors items-center border-1 h-[30px] rounded-[6px] border-[#1E1E1E0D]"
            >
              {/* <Icon name={tag.toLowerCase()} className="size-3.5" /> */}
              <SafeIcon
                name={tag.toLowerCase()}
                fallback="logo-heart-3"
                className="size-3.5"
              />

              <span>{tag}</span>

              {!readonly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(index);
                  }}
                >
                  <Icon
                    name="close"
                    className="hover:text-red-600 transition-colors text-black hover:cursor-pointer size-2 ml-1"
                  />
                </button>
              )}
            </Badge>
          ))}
        </div>

        {!isMaxReached && !readonly && (
          <div className="relative w-full flex justify-between items-center">
            <>
              <input
                style={noBorderStyles}
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  setIsOpen((prev) => {
                    const next = !prev;
                    if (next) inputRef.current?.focus();
                    return next;
                  });
                }}
                placeholder={showPlaceholder ? 'Related tags...' : ''}
                disabled={isMaxReached}
                className={cn(
                  'max-w-[110px] w-fit outline-none bg-transparent placeholder:text-black/30 md:placeholder:font-medium md:placeholder:text-base md:text-base text-sm h-fit placeholder:p-0',
                  {
                    'cursor-not-allowed': isMaxReached,
                    'md:w-5': remainingTags === 1,
                  }
                )}
              />
              <button
                type="button"
                onClick={() => {
                  if (isMaxReached) return;
                  setIsOpen((prev) => {
                    const next = !prev;
                    if (next) {
                      inputRef.current?.focus();
                    } else {
                      inputRef.current?.blur();
                    }
                    return next;
                  });
                }}
                className="text-gray-400 hover:text-gray-700 hover:cursor-pointer transition-colors"
              >
                <Icon
                  name="chevron-down"
                  className={cn('size-4', {
                    'rotate-180': isOpen,
                  })}
                />
              </button>
            </>
            <span className="text-sm font-light text-black/40">
              {remainingTags} tag{remainingTags !== 1 ? 's' : ''} remaining
            </span>

            {isOpen && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 mt-1 z-50 overflow-y-auto min-w-fit w-40 h-fit max-h-[148px] gap-1 border border-gray-200 bg-white shadow-lg rounded-md p-1"
              >
                {filteredTags.map((tag, index) => (
                  <button
                    key={tag.name}
                    type="button"
                    onClick={() => addTag(tag.name)}
                    className={cn(
                      'w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3 transition-colors text-sm',
                      { 'bg-gray-100': index === focusedIndex }
                    )}
                  >
                    <Icon
                      name={tag.icon}
                      width={16}
                      height={16}
                      className="text-gray-600"
                    />
                    <span>{highlightMatch(tag.name, inputValue)}</span>
                  </button>
                ))}
                {filteredTags.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No tags found
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
